import { useState, useEffect } from "react";
import "./AddProduct.css";
import dataService from "../services/dataService";
import { useNavigate, useParams } from "react-router-dom";
import { useToast } from "../context/ToastContext";
import { 
  Settings, 
  Plus, 
  X, 
  Trash2
} from "lucide-react";

function AddProductScreen() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    sku: "",
    category: "technology",
    description: "",
    wholesalePrice: 0,
    retailPrice: 0,
    costPrice: 0,
    stockCount: 0,
    minimumStock: 0,
    unit: "pcs",
    status: "active",
    availability: "Instock",
    warehouseLocation: "Main Warehouse - dubai",
    barcode: "",
    weight: 0,
    dimensions: "",
    taxRate: 5,
    hsCode: "",
    variationType: "",
    variationValue: ""
  });
  const [imageFile, setImageFile] = useState(null);
  const [currentImageUrl, setCurrentImageUrl] = useState("");
  const [existingCategories, setExistingCategories] = useState([]);
  const [isAddingNewCategory, setIsAddingNewCategory] = useState(false);
  const [isManageCategoriesOpen, setIsManageCategoriesOpen] = useState(false);
  const [deletingCategory, setDeletingCategory] = useState(false);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const res = await dataService.getProducts();
        const cats = [...new Set(res.data.data.map(p => p.category).filter(Boolean))];
        setExistingCategories(cats);
      } catch (err) {
        console.error("Failed to fetch categories", err);
      }
    };
    loadCategories();
  }, []);

  useEffect(() => {
    if (isEditMode) {
      const fetchProduct = async () => {
        try {
          const response = await dataService.getProducts();
          const product = response.data.data.find(p => p._id === id);
          if (product) {
            setFormData({
              name: product.name || "",
              sku: product.sku || "",
              category: product.category || "technology",
              description: product.description || "",
              wholesalePrice: product.wholesalePrice || 0,
              retailPrice: product.retailPrice || 0,
              costPrice: product.costPrice || 0,
              stockCount: product.stockCount || 0,
              minimumStock: product.minimumStock || 0,
              unit: product.unit || "pcs",
              status: product.status || "active",
              availability: product.availability || "Instock",
              warehouseLocation: product.warehouseLocation || "Main Warehouse - dubai",
              barcode: product.barcode || "",
              weight: product.weight || 0,
              dimensions: product.dimensions || "",
              taxRate: product.taxRate || 5,
              hsCode: product.hsCode || "",
              variationType: product.variations?.[0]?.type || "",
              variationValue: product.variations?.[0]?.value || ""
            });
            setCurrentImageUrl(product.imageUrl || "");
          } else {
            setError("Product not found");
          }
        } catch (err) {
          setError("Failed to fetch product details");
        } finally {
          // done
        }
      };
      fetchProduct();
    }
  }, [id, isEditMode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    setImageFile(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    const convertToBase64 = (file) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
      });
    };

    try {
      const payload = {
        ...formData,
        variations: formData.variationType ? [{ type: formData.variationType, value: formData.variationValue }] : []
      };

      if (imageFile) {
        payload.imageUrl = await convertToBase64(imageFile);
      }

      if (isEditMode) {
        await dataService.updateProduct(id, payload);
        addToast("Product updated successfully", "success");
      } else {
        await dataService.addProduct(payload);
        addToast("Product created successfully", "success");
      }
      navigate("/products");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to create product");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCategory = async (catName) => {
    if (!window.confirm(`Are you sure you want to delete the category "${catName}"? This will remove it from all products.`)) return;
    
    setDeletingCategory(true);
    try {
      await dataService.deleteCategory(catName);
      addToast(`Category "${catName}" deleted`, "success");
      const updatedCats = existingCategories.filter(c => c !== catName);
      setExistingCategories(updatedCats);
      if (formData.category === catName) {
        setFormData(prev => ({ ...prev, category: "" }));
      }
    } catch (err) {
      addToast("Failed to delete category", "error");
    } finally {
      setDeletingCategory(false);
    }
  };

  return (
    <div className="add-vendor-page">
      <div className="page-header">
        <h1>{isEditMode ? "Edit Product" : "Add New Product"}</h1>
        <p>{isEditMode ? `Updating ${formData.name}` : "Enter product details and configuration"}</p>
      </div>

      {error && <div className="error-message" style={{ color: 'red', marginBottom: '20px' }}>{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="vendor-grid">
          <div className="vendor-left">
            <div className="vendor-card">
              <h2>Basic Information</h2>
              <div className="v-name">
                <div>
                  <p>Product Name</p>
                  <input name="name" value={formData.name} onChange={handleChange} required placeholder="enter product name" />
                </div>
                <div>
                  <p>SKU</p>
                  <input name="sku" value={formData.sku} onChange={handleChange} required placeholder="Enter SKU" />
                </div>
              </div>

              <div className="v-name">
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <p style={{ margin: 0 }}>Category</p>
                    <div style={{ display: 'flex', gap: '12px' }}>
                      <button 
                        type="button" 
                        onClick={() => setIsManageCategoriesOpen(true)}
                        style={{ 
                          background: 'none', 
                          border: 'none', 
                          color: '#6B7280', 
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: '500',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                      >
                        <Settings size={14} /> Manage
                      </button>
                      <button 
                        type="button" 
                        onClick={() => setIsAddingNewCategory(!isAddingNewCategory)}
                        style={{ 
                          background: 'none', 
                          border: 'none', 
                          color: 'var(--primary-color)', 
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: '500',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                      >
                        {isAddingNewCategory ? <><X size={14} /> Cancel</> : <><Plus size={14} /> New Category</>}
                      </button>
                    </div>
                  </div>
                  {isAddingNewCategory ? (
                    <input 
                      name="category" 
                      value={formData.category} 
                      onChange={handleChange} 
                      required 
                      placeholder="Enter new category" 
                      autoFocus
                    />
                  ) : (
                    <select name="category" value={formData.category} onChange={handleChange}>
                      <option value="">Select Category</option>
                      {existingCategories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                      {/* Add default ones if empty or just rely on what's in DB */}
                      {!existingCategories.includes("technology") && <option value="technology">technology</option>}
                      {!existingCategories.includes("drinkware") && <option value="drinkware">drinkware</option>}
                      {!existingCategories.includes("apparel") && <option value="apparel">apparel</option>}
                      {/* ... other defaults if needed ... */}
                    </select>
                  )}
                </div>
              </div>

              <div className="v-name">
                <div>
                  <p>Description</p>
                  <input name="description" value={formData.description} onChange={handleChange} placeholder="enter product description" />
                </div>
              </div>

              <div className="v-name">
                <div>
                  <p>Product Image</p>
                  <input type="file" accept="image/*" onChange={handleFileChange} />
                  {imageFile ? (
                    <div style={{ marginTop: '10px' }}>
                      <img
                        src={URL.createObjectURL(imageFile)}
                        alt="Preview"
                        style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '8px' }}
                      />
                      <p style={{ fontSize: '11px', color: '#059669', marginTop: '4px' }}>New image selected</p>
                    </div>
                  ) : currentImageUrl && (
                    <div style={{ marginTop: '10px' }}>
                      <img
                        src={dataService.getImageUrl(currentImageUrl)}
                        alt="Current"
                        style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '8px' }}
                      />
                      <p style={{ fontSize: '11px', color: '#6B7280', marginTop: '4px' }}>Current image</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="vendor-card">
              <h2>Pricing & Stock</h2>
              <div className="v-name">
                <div>
                  <p>Wholesale price</p>
                  <input type="number" name="wholesalePrice" value={formData.wholesalePrice} onChange={handleChange} placeholder="0.00" />
                </div>
                <div>
                  <p>Retail Price</p>
                  <input type="number" name="retailPrice" value={formData.retailPrice} onChange={handleChange} placeholder="0.00" />
                </div>
                <div>
                  <p>Cost price</p>
                  <input type="number" name="costPrice" value={formData.costPrice} onChange={handleChange} placeholder="0.00" />
                </div>
              </div>

              <div className="v-name">
                <div>
                  <p>Current Stock</p>
                  <input type="number" name="stockCount" value={formData.stockCount} onChange={handleChange} placeholder="0.00" />
                </div>
                <div>
                  <p>Minimum Stock</p>
                  <input type="number" name="minimumStock" value={formData.minimumStock} onChange={handleChange} placeholder="0.00" />
                </div>
                <div>
                  <p>Unit</p>
                  <input name="unit" value={formData.unit} onChange={handleChange} placeholder="e.g. pcs, kg" />
                </div>
              </div>
            </div>

            <div className="vendor-card">
              <h2>Product Variations</h2>
              <div className="v-name">
                <div>
                  <p>Variation Type</p>
                  <input name="variationType" value={formData.variationType} onChange={handleChange} placeholder="e.g size, color" />
                </div>
                <div>
                  <p>Value</p>
                  <input name="variationValue" value={formData.variationValue} onChange={handleChange} placeholder="e.g, Large, Red" />
                </div>
              </div>
            </div>
          </div>

          <div className="vendor-right">
            <div className="vendor-card">
              <div className="v-namer">
                <h2>Product Status</h2>
                <div>
                  <p>Status</p>
                  <select name="status" value={formData.status} onChange={handleChange}>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="draft">Draft</option>
                  </select>
                  <p>Availability</p>
                  <select name="availability" value={formData.availability} onChange={handleChange}>
                    <option value="Instock">Instock</option>
                    <option value="Outofstock">Out of stock</option>
                    <option value="Pre-Order">Pre-Order</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="vendor-card">
              <div className="v-namer">
                <h2>Inventory Details</h2>
                <div>
                  <p>Warehouse Location</p>
                  <select name="warehouseLocation" value={formData.warehouseLocation} onChange={handleChange}>
                    <option value="Main Warehouse - dubai">Main Warehouse - dubai</option>
                    <option value="West Coast Hub - sharjah">West Coast Hub - sharjah</option>
                  </select>
                  <p>Barcode</p>
                  <input name="barcode" value={formData.barcode} onChange={handleChange} placeholder="enter barcode" />
                  <p>Weight (kg)</p>
                  <input type="number" name="weight" value={formData.weight} onChange={handleChange} placeholder="0.00" />
                  <p>Dimensions</p>
                  <input name="dimensions" value={formData.dimensions} onChange={handleChange} placeholder="e.g 30*20*10" />
                </div>
              </div>
            </div>

            <div className="vendor-card">
              <div className="v-namer">
                <h2>Tax & compliance</h2>
                <div>
                  <p>Tax rate %</p>
                  <input type="number" name="taxRate" value={formData.taxRate} onChange={handleChange} placeholder="10" />
                  <p>HS code</p>
                  <input name="hsCode" value={formData.hsCode} onChange={handleChange} placeholder="enter HS code" />
                </div>
              </div>
            </div>

            <div className="vendor-actions">
              <button type="button" className="secondary-btn" onClick={() => navigate("/products")}>Cancel</button>
              <button type="submit" className="primary-btn" disabled={loading}>
                {loading ? "Saving..." : (isEditMode ? "Update Product" : "Create Product")}
              </button>
            </div>
          </div>
        </div>
      </form>

      {/* Category Management Modal */}
      {isManageCategoriesOpen && (
        <div className="modal-overlay" onClick={() => setIsManageCategoriesOpen(false)} style={{ zIndex: 1000 }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <div className="modal-header">
              <h2>Manage Categories</h2>
              <button className="close-btn" onClick={() => setIsManageCategoriesOpen(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: '13px', color: '#6B7280', marginBottom: '15px' }}>
                Deleting a category will remove it from all products currently assigned to it.
              </p>
              <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {existingCategories.length === 0 ? (
                  <p style={{ textAlign: 'center', padding: '20px', color: '#9CA3AF' }}>No custom categories found.</p>
                ) : (
                  existingCategories.map(cat => (
                    <div key={cat} style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center', 
                      padding: '10px', 
                      borderBottom: '1px solid #F3F4F6' 
                    }}>
                      <span style={{ fontWeight: '500' }}>{cat}</span>
                      <button 
                        type="button"
                        onClick={() => handleDeleteCategory(cat)}
                        disabled={deletingCategory}
                        style={{ 
                          background: 'none', 
                          border: 'none', 
                          color: '#EF4444', 
                          cursor: 'pointer',
                          fontSize: '14px'
                        }}
                        title="Delete Category"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="primary-btn" onClick={() => setIsManageCategoriesOpen(false)}>Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AddProductScreen;
